import axios, { type AxiosError } from 'axios'

import { captureEvent } from './posthogTelemetry'

type FailureKind = 'client' | 'server' | 'network'

interface HttpRequestFailure {
  kind: FailureKind
  statusCode?: number
}

// Other 4xx (401, 403, 404, 409, 422) are expected outcomes handled in the UI - not worth reporting.
const REPORTED_CLIENT_STATUSES = new Set([400, 408, 429])
const ID_SEGMENT = /^(\d+|[0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

function normalizeEndpoint(url: string | undefined): string {
  if (!url) return 'unknown'

  return url
    .split('?')[0]
    .split('/')
    .map(segment => (ID_SEGMENT.test(segment) ? ':id' : segment))
    .join('/')
}

export function classifyHttpFailure(error: unknown): HttpRequestFailure | null {
  if (!axios.isAxiosError(error) || axios.isCancel(error)) return null

  const statusCode = error.response?.status

  if (statusCode === undefined) {
    // Offline is a connectivity issue on the user's side - nothing to learn from it.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return null

    return { kind: 'network' }
  }

  if (statusCode >= 500) return { kind: 'server', statusCode }
  if (REPORTED_CLIENT_STATUSES.has(statusCode)) return { kind: 'client', statusCode }

  return null
}

export function reportHttpRequestFailure(error: AxiosError): void {
  try {
    const failure = classifyHttpFailure(error)

    if (!failure) return

    captureEvent('http_request_failed', {
      failure_kind: failure.kind,
      status_code: failure.statusCode,
      method: error.config?.method?.toUpperCase() ?? 'unknown',
      endpoint: normalizeEndpoint(error.config?.url),
      error_code: error.code
    })
  } catch {
    // Telemetry must never break request handling.
  }
}
